import type { Scene } from "@babylonjs/core/scene";
import type { PBRMaterial } from "@babylonjs/core/Materials/PBR/pbrMaterial";
import { ParticleSystem } from "@babylonjs/core/Particles/particleSystem";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Color4 } from "@babylonjs/core/Maths/math.color";
import { createSoftParticleTexture } from "../../core/particleTextures";
import { gameEvents } from "../../core/EventBus";
import { GameEvents } from "../../config/events";
import { CAMERA } from "../../config/GameConfig";
import { AccessibilitySystem } from "../../systems/AccessibilitySystem";
import { shouldCullAmbient } from "../../systems/AmbientCull";
import { particleDensityFactor } from "../../systems/particleScaling";

type Phase = "irrigation" | "incubation";

/** Presentation only: the cycle still owns every phase, duration and rule. */
export class ConservatoryAtmosphere {
  readonly particleSystems: readonly ParticleSystem[];
  private readonly texture;
  private readonly floors: Array<{ material: PBRMaterial; dry: number }>;
  private readonly unsubs: Array<() => void>;
  private readonly sceneDispose;
  private disposed = false;
  private culled = false;
  private wet: number;
  private from: number;
  private target: number;
  private elapsed = 0;
  private duration = 0;

  constructor(private readonly scene: Scene, floors: PBRMaterial[], initial: Phase) {
    this.wet = this.from = this.target = initial === "irrigation" ? 1 : 0;
    this.floors = [...new Set(floors)].map(material => ({ material, dry: material.roughness ?? 1 }));
    this.texture = createSoftParticleTexture(scene, "conservatoryAtmosphereSoft");
    const mist = this.makeSystem("conservatoryRearMist", 64, .34, -5.65,
      [1, 1.8], [2, 4], [new Color4(.66, .77, .77, .09), new Color4(.54, .68, .71, .06)]);
    mist.createBoxEmitter(new Vector3(-.2, .08, 0), new Vector3(.2, .2, .02),
      new Vector3(-10, 0, -.15), new Vector3(10, .3, .15));
    const steam = this.makeSystem("conservatoryPipeSteam", 32, .62, -5.95,
      [.16, .4], [.6, 1.6], [new Color4(.88, .83, .72, .12), new Color4(.75, .76, .72, .07)]);
    steam.createBoxEmitter(new Vector3(-.05, .7, 0), new Vector3(.05, 1, .01),
      new Vector3(-9, 0, -.04), new Vector3(9, .05, .04));
    const dust = this.makeSystem("conservatoryRearMotes", 48, 1, -4.9,
      [.014, .03], [2, 4], [new Color4(.8, .77, .63, .16), new Color4(.65, .73, .68, .1)]);
    dust.createBoxEmitter(new Vector3(-.2, .1, 0), new Vector3(.2, .4, .01),
      new Vector3(-10, 0, -.1), new Vector3(10, 5, .1));
    this.particleSystems = [mist, steam, dust];
    this.unsubs = [
      gameEvents.on(GameEvents.CONSERVATORY_CYCLE_TRANSITION, ({ to, durationMs }) => {
        this.from = this.wet; this.target = to === "irrigation" ? 1 : 0;
        this.elapsed = 0; this.duration = Math.max(0, durationMs);
      }),
      gameEvents.on(GameEvents.CONSERVATORY_PHASE_CHANGED, ({ phase }) => {
        this.wet = this.from = this.target = phase === "irrigation" ? 1 : 0;
        this.duration = 0; this.apply();
      }),
      gameEvents.on(GameEvents.ACCESSIBILITY_CHANGED, () => this.apply()),
    ];
    this.sceneDispose = scene.onDisposeObservable.add(() => this.dispose());
    this.apply();
  }

  private makeSystem(name: string, capacity: number, y: number, z: number,
    size: [number, number], life: [number, number], colors: [Color4, Color4]): ParticleSystem {
    const ps = new ParticleSystem(name, capacity, this.scene);
    ps.particleTexture = this.texture;
    ps.emitter = new Vector3(7.5, y, z);
    [ps.minSize, ps.maxSize] = size;
    [ps.minLifeTime, ps.maxLifeTime] = life;
    [ps.color1, ps.color2] = colors;
    ps.colorDead = new Color4(colors[0].r, colors[0].g, colors[0].b, 0);
    ps.minEmitPower = .08; ps.maxEmitPower = .18;
    ps.gravity = Vector3.Zero();
    ps.blendMode = ParticleSystem.BLENDMODE_STANDARD;
    ps.emitRate = 0;
    ps.start();
    return ps;
  }

  /** Called on the existing simulation clock; affects no gameplay state. */
  update(dtMs: number, viewX: number, cameraZ: number): void {
    if (this.disposed) return;
    if (this.duration > 0) {
      this.elapsed = Math.min(this.duration, this.elapsed + Math.max(0, dtMs));
      const t = this.elapsed / this.duration;
      this.wet = this.from + (this.target - this.from) * t;
      if (t === 1) this.duration = 0;
    }
    this.culled = shouldCullAmbient(Math.abs(cameraZ), CAMERA.AMBIENT_CULL_DISTANCE,
      CAMERA.AMBIENT_CULL_HYSTERESIS, this.culled);
    // Stay in the measured glasshouse, behind the lane and at the pipe beds.
    const x = Math.max(-18, Math.min(158, viewX));
    for (const ps of this.particleSystems) (ps.emitter as Vector3).x = x;
    this.apply();
  }

  private apply(): void {
    if (this.disposed) return;
    const factor = this.culled ? 0 : particleDensityFactor(AccessibilitySystem.getInstance().state.reducedMotion);
    this.particleSystems[0].emitRate = 8 * this.wet * factor;
    this.particleSystems[1].emitRate = 6 * (1 - this.wet) * factor;
    this.particleSystems[2].emitRate = 4 * factor;
    for (const { material, dry } of this.floors) material.roughness = Math.max(.6, dry - .17 * this.wet);
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    for (const unsub of this.unsubs) unsub();
    this.scene.onDisposeObservable.remove(this.sceneDispose);
    for (const ps of this.particleSystems) { ps.stop(); ps.dispose(false); }
    this.texture.dispose();
    for (const { material, dry } of this.floors) material.roughness = dry;
  }
}
